import * as React from 'react';
import { IndexRoute, Route } from 'react-router';

import { Route as CourseraRoute } from '@coursera/react-router';

import loadOnRoute from 'bundles/common/components/loadOnRoute';
import CourseraPlusHubApp from 'bundles/coursera-plus-landing-page/CourseraPlusHubApp';
import { generateRedesignExperimentImpression } from 'bundles/coursera-plus-landing-page/utils/landingPageExperiments';
import NotFound from 'bundles/phoenix/components/NotFound';

type ChunkImport = () => Promise<{ default: React.ComponentType } | React.ComponentType>;

// Routes that participate in the Coursera Plus landing-page redesign A/B test
// load their redesign and control variants from separate webpack chunks, so
// users in the control arm never download the redesign's transitive deps
// (PlansStack, promo-template, etc.). The experiment flag is evaluated (and the
// impression recorded) per request inside the `loadOnRoute` factory —
// react-router's `match()` awaits this before SSR begins, so the assigned
// variant is fully rendered into the SSR HTML (preserves SEO and A/B test
// parity). See MONETIZE-2750 / MONETIZE-2683.
const loadRedesignExperimentSplit = (redesignImport: ChunkImport, controlImport: ChunkImport) =>
  loadOnRoute(() => (generateRedesignExperimentImpression() ? redesignImport() : controlImport()));

// Default Coursera Plus landing page (/courseraplus).
const LandingPage = loadRedesignExperimentSplit(
  () =>
    import(
      /* webpackChunkName: "coursera-plus-landing-page-redesign" */
      'bundles/coursera-plus-landing-page/components/LandingPageRedesign'
    ),
  () =>
    import(
      /* webpackChunkName: "coursera-plus-landing-page-control" */
      'bundles/coursera-plus-landing-page/components/LandingPage'
    )
);

// Coursera Plus promo pages (/courseraplus/special/:slug) are different from
// the default landing page. Both pages are scoped in the redesign project.
const CourseraPlusMarketingHome = loadRedesignExperimentSplit(
  () =>
    import(
      /* webpackChunkName: "coursera-plus-marketing-home-redesign" */
      'bundles/coursera-plus-landing-page/components/MarketingHomeRedesign'
    ),
  () =>
    import(
      /* webpackChunkName: "coursera-plus-marketing-home-control" */
      'bundles/coursera-plus-landing-page/components/MarketingHome'
    )
);

export default (
  <CourseraRoute
    component={CourseraPlusHubApp}
    path="/"
    handle={{
      tracking: { data: () => ({ page: { type: 'cplus_description' } }) },
    }}
  >
    <Route path="courseraplus">
      <IndexRoute getComponent={LandingPage} />
      <Route getComponent={LandingPage} path="subscribe/:id" />
      <Route getComponent={CourseraPlusMarketingHome} path="special/:slug" />
    </Route>

    <Route component={NotFound} path="*" />
  </CourseraRoute>
);
